import React, { useState, useRef } from 'react';
import { PaperAirplaneIcon, PaperClipIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useAuthStore } from '../hooks/useAuthStore';
import { attachmentsAPI, reportsAPI } from '../services/api';
import FileAttachment from '../components/FileAttachment';
import { notify } from '../hooks/useNotificationStore';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const ALLOWED_TYPES = new Set([
  'image/png', 'image/jpeg', 'image/gif', 'image/webp',
  'application/pdf', 'text/plain', 'text/csv', 'text/markdown',
  'text/x-python', 'text/javascript', 'text/html', 'text/css',
  'application/json', 'application/xml',
]);

interface PendingFile {
  file: File;
  previewUrl?: string;
}

export default function Report() {
  const { user } = useAuthStore();
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [category, setCategory] = useState<'bug' | 'feature' | 'other'>('bug');
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea
  React.useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(
        textareaRef.current.scrollHeight,
        300
      )}px`;
    }
  }, [message]);

  // Handle clipboard paste for images
  React.useEffect(() => {
    const handlePaste = async (e: ClipboardEvent) => {
      if (!textareaRef.current?.contains(e.target as Node)) return;

      const items = Array.from(e.clipboardData?.items || []);
      const imageItems = items.filter(item => item.type.startsWith('image/'));

      if (imageItems.length > 0) {
        e.preventDefault();

        for (const item of imageItems) {
          const file = item.getAsFile();
          if (file) {
            // Create a meaningful filename with timestamp
            const timestamp = new Date().toISOString().slice(0, 16).replace(/[:-]/g, '');
            const extension = file.type.split('/')[1] || 'png';
            const filename = `report_screenshot_${timestamp}.${extension}`;

            // Create a new File with proper name
            const namedFile = new File([file], filename, { type: file.type });

            // Add to pending files
            const previewUrl = namedFile.type.startsWith('image/')
              ? URL.createObjectURL(namedFile)
              : undefined;

            setPendingFiles(prev => [...prev, { file: namedFile, previewUrl }]);
          }
        }
      }
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, []);

  // Clean up preview URLs on unmount
  React.useEffect(() => {
    return () => {
      pendingFiles.forEach((pf) => {
        if (pf.previewUrl) URL.revokeObjectURL(pf.previewUrl);
      });
    };
  }, []);

  const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    setUploadError(null);
    const newPending: PendingFile[] = [];

    for (const file of Array.from(files)) {
      if (file.size > MAX_FILE_SIZE) {
        setUploadError(`"${file.name}" exceeds 10 MB limit`);
        continue;
      }
      if (!ALLOWED_TYPES.has(file.type) && file.type !== '') {
        // Check extension fallback for files with empty mime type
        const ext = file.name.split('.').pop()?.toLowerCase();
        const extAllowed = ['py', 'js', 'ts', 'jsx', 'tsx', 'html', 'css', 'json', 'xml', 'md', 'csv', 'txt', 'pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp'];
        if (!ext || !extAllowed.includes(ext)) {
          setUploadError(`"${file.name}" is not a supported file type`);
          continue;
        }
      }

      const previewUrl = file.type.startsWith('image/')
        ? URL.createObjectURL(file)
        : undefined;

      newPending.push({ file, previewUrl });
    }

    setPendingFiles((prev) => [...prev, ...newPending]);
    // Reset the input so the same file can be re-selected
    e.target.value = '';
  };

  const removePendingFile = (index: number) => {
    setPendingFiles((prev) => {
      const removed = prev[index];
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!subject.trim() || !message.trim() || isSubmitting) return;

    setIsSubmitting(true);
    setUploadError(null);

    try {
      let attachmentIds: number[] = [];

      // Upload files if any
      if (pendingFiles.length > 0) {
        try {
          const response = await attachmentsAPI.upload(
            pendingFiles.map((pf) => pf.file)
          );
          attachmentIds = response.data.attachments.map((a: { id: number }) => a.id);
        } catch (err: any) {
          setUploadError(err.response?.data?.detail || 'Failed to upload files');
          setIsSubmitting(false);
          return;
        }
      }

      // Send the report via API
      await reportsAPI.send({
        category,
        subject,
        message,
        attachment_ids: attachmentIds
      });

      // Clean up preview URLs
      pendingFiles.forEach((pf) => {
        if (pf.previewUrl) URL.revokeObjectURL(pf.previewUrl);
      });

      // Reset form
      setSubject('');
      setMessage('');
      setCategory('bug');
      setPendingFiles([]);

      notify.success('Report submitted successfully! We\'ll review it and get back to you.');

    } catch (error: any) {
      notify.error('Failed to submit report. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <ExclamationTriangleIcon className="w-8 h-8 text-primary-500" />
          <h1 className="text-2xl font-bold text-surface-100">Report Issues & Feedback</h1>
        </div>

        <div className="card max-w-2xl">
          <div className="mb-6">
            <p className="text-surface-300 mb-4">
              Found a bug? Have a feature suggestion? We'd love to hear from you!
              Your feedback helps us improve Quorum for everyone.
            </p>
            <div className="bg-surface-700 rounded-lg p-4 text-sm text-surface-400">
              <p><strong className="text-surface-200">Email:</strong> mehoeppner@gmail.com</p>
              <p><strong className="text-surface-200">From:</strong> {user?.username}</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-2">
                Category
              </label>
              <div className="flex gap-4">
                {[
                  { id: 'bug', label: 'Bug Report', desc: 'Something isn\'t working correctly' },
                  { id: 'feature', label: 'Feature Request', desc: 'Suggest an improvement' },
                  { id: 'other', label: 'Other', desc: 'General feedback or questions' }
                ].map((cat) => (
                  <label key={cat.id} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="category"
                      value={cat.id}
                      checked={category === cat.id}
                      onChange={(e) => setCategory(e.target.value as any)}
                      className="w-4 h-4 text-primary-600 bg-surface-700 border-surface-600 focus:ring-primary-500"
                    />
                    <div>
                      <span className="text-surface-200 text-sm">{cat.label}</span>
                      <p className="text-xs text-surface-500">{cat.desc}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Subject */}
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-1">
                Subject *
              </label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Brief description of the issue or suggestion"
                className="input-field"
                required
                maxLength={200}
              />
            </div>

            {/* Upload error */}
            {uploadError && (
              <div className="p-2 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-xs text-red-400">{uploadError}</p>
              </div>
            )}

            {/* Attached files */}
            {pendingFiles.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-surface-300 mb-2">
                  Attached Files
                </label>
                <div className="flex flex-wrap gap-2">
                  {pendingFiles.map((pf, i) => (
                    <FileAttachment
                      key={`${pf.file.name}-${i}`}
                      filename={pf.file.name}
                      fileSize={pf.file.size}
                      mimeType={pf.file.type}
                      previewUrl={pf.previewUrl}
                      onRemove={() => removePendingFile(i)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Message */}
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-1">
                Message *
              </label>
              <div className="relative">
                <textarea
                  ref={textareaRef}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Describe the issue in detail. Include steps to reproduce, expected behavior, or details about your suggestion..."
                  className="input-field min-h-[120px] max-h-[300px] resize-none"
                  required
                  rows={5}
                />
                <div className="absolute bottom-2 right-2 flex gap-1">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="p-1.5 rounded hover:bg-surface-600 text-surface-400 hover:text-surface-200 transition-colors"
                    title="Attach file"
                  >
                    <PaperClipIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              accept="image/png,image/jpeg,image/gif,image/webp,application/pdf,text/plain,text/csv,text/markdown,.py,.js,.ts,.jsx,.tsx,.html,.css,.json,.xml,.md"
              onChange={handleFilesSelected}
            />

            {/* Submit */}
            <div className="flex items-center gap-4">
              <button
                type="submit"
                disabled={!subject.trim() || !message.trim() || isSubmitting}
                className={`flex items-center gap-2 px-6 py-2 rounded-lg font-medium transition-colors ${
                  subject.trim() && message.trim() && !isSubmitting
                    ? 'bg-primary-600 text-white hover:bg-primary-700'
                    : 'bg-surface-600 text-surface-400 cursor-not-allowed'
                }`}
              >
                {isSubmitting ? (
                  <div className="w-4 h-4 border-2 border-surface-400 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <PaperAirplaneIcon className="w-4 h-4" />
                )}
                {isSubmitting ? 'Submitting...' : 'Submit Report'}
              </button>

              <p className="text-xs text-surface-500">
                Reports are sent to the development team for review
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}